import { useEffect, useMemo, useRef } from 'react';

import { useRouter } from 'expo-router';
import { useMinimalCartUi } from 'features/cart/hooks/use-minimal-cart-ui';
import { useStoreContext } from 'features/store';
import { hasShownStoreChangeDialog } from 'features/store/constants';

import { ILocation } from '@rbi-ctg/frontend';
import { ServiceMode } from 'generated/graphql-gateway';
import { useDistanceText } from 'hooks/geolocation/use-distance-text';
import { useOrderContext } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import { routes } from 'utils/routing';

import { useCheckoutContext } from '../../hooks/use-checkout-context';
import { useCheckoutPriceOrder } from '../../hooks/use-checkout-price-order';
import { UseStoreLocatorWithMapReturn } from '../../types';
import { useStoreChangeDialog } from '../change-store-confirmation-dialog/use-store-change-dialog';

export const useStoreLocatorWithMap = (): UseStoreLocatorWithMapReturn => {
  const { deliveryAddress } = useOrderContext();
  const { serviceMode } = useServiceModeContext();
  const { noStoreSelected, store: restaurant } = useStoreContext();
  const { serverOrder } = useCheckoutContext();
  const enableMinimalCartUi = useMinimalCartUi();
  const { priceCheckoutOrderForServiceMode } = useCheckoutPriceOrder();
  const isDelivery = serviceMode === ServiceMode.DELIVERY;
  const router = useRouter();
  const prevStoreNumberRef = useRef<string | null>(null);
  const currentStoreNumber = restaurant?.number ?? null;

  const handleNavigateToStoreLocator = () => {
    router.navigate({
      pathname: isDelivery ? routes.deliveryAddress : routes.storeLocator,
      params: {
        redirectUrl: routes.cartCheckout,
      },
    });
    hasShownStoreChangeDialog.current = true;
  };

  const storeChangeDialogProps = useStoreChangeDialog(handleNavigateToStoreLocator);

  const { distanceText, isLoading: isLoadingDistance, canShowDistance } = useDistanceText({
    restaurant,
    shouldUseUserCoordinatesIfAvailable: true,
  });

  useEffect(() => {
    // Track store changes while on checkout. If Minimal Cart UI is enabled, trigger a reprice
    // so `pricedOrderId` updates and checkout can refetch the repriced order.
    if (!currentStoreNumber) {
      return;
    }

    if (prevStoreNumberRef.current === null) {
      prevStoreNumberRef.current = currentStoreNumber;
      return;
    }

    const storeChanged = prevStoreNumberRef.current !== currentStoreNumber;
    if (!storeChanged) {
      return;
    }

    prevStoreNumberRef.current = currentStoreNumber;

    if (!enableMinimalCartUi) {
      return;
    }

    const orderId = serverOrder?.rbiOrderId ?? null;
    if (!orderId || !serviceMode) {
      return;
    }

    priceCheckoutOrderForServiceMode(serviceMode);
  }, [
    currentStoreNumber,
    enableMinimalCartUi,
    priceCheckoutOrderForServiceMode,
    serverOrder?.rbiOrderId,
    serviceMode,
  ]);

  const addressLine1 = isDelivery
    ? deliveryAddress?.addressLine1 || ''
    : restaurant?.physicalAddress?.address1 || '';

  const cityStateZip = isDelivery
    ? `${deliveryAddress?.city || ''}, ${deliveryAddress?.state || ''} ${
        deliveryAddress?.zip || ''
      }`
    : `${restaurant?.physicalAddress?.city || ''}, ${
        restaurant?.physicalAddress?.stateProvince || ''
      } ${restaurant?.physicalAddress?.postalCode || ''}`;

  const restaurantCoordinates: ILocation | undefined = useMemo(
    () =>
      restaurant.latitude && restaurant.longitude
        ? {
            lat: restaurant.latitude,
            lng: restaurant.longitude,
          }
        : undefined,
    [restaurant.latitude, restaurant.longitude]
  );

  return {
    noStoreSelected,
    addressLine1,
    cityStateZip,
    handleChangeStore: storeChangeDialogProps.openDialog,
    isLoadingDistance,
    distanceText,
    canShowDistance,
    restaurantCoordinates,
    dialogProps: storeChangeDialogProps,
  };
};
